import React, { ReactNode } from "react";
import { Button } from "react-bootstrap";
import { dispatchPerunNotice, infoNotice } from "./events/Notice";
import {
  TextInput,
  RadioInput,
  ImageInput,
  SecretInput,
  CheckboxInput,
} from "./inputs";
import { getInObject, pathAsState, State } from "../client";
import { BuilderMetadata } from "../client/Metadata";
import { FileInput } from "./inputs";
import { IpfsSettings } from "../client/Storage";

export interface FormData {
  metadata: BuilderMetadata;
  chain: string;
  storage: string;
  ipfs: IpfsSettings; // Additional settings for IPFS storage
}

interface Props {
  state: State<FormData>;
}

export function CreateCNFTForm({ state }: Props) {
  const row_chain = row(
    "destination",
    "Minting destination",
    "Select an option for Minting destination:",
    "Erdstall is a Layer 2 solution, Ethereum is a Layer 1 solution, Polkadot is a Layer 0 solution",
    <RadioInput state={pathAsState(state, "chain")} options={["Sepolia"]} />,
  );
  const row_storage = row(
    "storage",
    "Storage",
    "Where do you want us to store your data?",
    "AWS is a centralized storage solution, IPFS is a decentralized storage solution",
    <RadioInput
      state={pathAsState(state, "storage")}
      options={["Keyserver", "IPFS", "Backblaze B2"]}
    />,
  );
  // SECURITY: Do not provide the pinata_jwt in any way. It is readable in the
  // front-end. The user is supposed to add his own JWT, that way any costs and
  // usage restrictions are on his account an not ours.
  const row_ipfs_pinning = row(
    "ipfs-pinning",
    "Pinning",
    "Which IPFS Pinning Service(s)?",
    "Without pinning the data may be lost at any point",
    <div className="checkbox-select">
      <label>
        <CheckboxInput state={pathAsState(state, "ipfs.pinata")} />
        Pinata
      </label>
      <div>
        <SecretInput
          state={pathAsState(state, "ipfs.pinata_jwt")}
          disabled={!getInObject(state, "ipfs.pinata")}
          placeholder="JWT"
        />
        <TextInput
          state={pathAsState(state, "ipfs.pinata_gateway")}
          disabled={!getInObject(state, "ipfs.pinata")}
          placeholder="Gateway"
        />
      </div>
    </div>,
  );
  // TODO: We probably want to style this differently to indicate it isn't an input.
  const row_availability = (
    <tr key="availability" className="availability-row">
      <td>The data will be available as long as:</td>
      <td className="availability">
        <ul>
          <li>The keyserver is running OR</li>
          <li>You have access to the exported key OR</li>
          <li>Your browsers local storage isn't cleared (this website)</li>
        </ul>
        AND you have access to the metadata file:
        <ul>
          <li>You have stored a copy locally OR</li>
          {state[0].storage === "Keyserver" && (
            <li>
              The <b>Keyserver</b> stores the it <b>(30 days)</b>
            </li>
          )}
          {state[0].storage === "Backblaze B2" && (
            <li>
              <b>Our Backblaze B2</b> bucket stores it <b>(30 days)</b>
            </li>
          )}
          {state[0].storage === "IPFS" && (
            <li>
              <b>Any IPFS node</b> stores it
            </li>
          )}
        </ul>
      </td>
      <td></td>
    </tr>
  );
  const row_title = row(
    "title",
    "Title",
    "What is the title of your cNFT?",
    "Identifies the asset to which the cNFT represents",
    <TextInput state={pathAsState(state, "metadata.name$confidential")} />,
  );
  const row_description = row(
    "description",
    "Description",
    "What is the description of your cNFT?",
    "Describes the title of your cNFT",
    <TextInput
      state={pathAsState(state, "metadata.description$confidential")}
    />,
  );
  const row_preview = row(
    "preview",
    "Preview",
    "Upload the file you want as a preview, if wanted.",
    "You can upload a file with the following extensions: .pdf, .doc, .docx, .txt, .jpg, .png, .jpeg",
    <ImageInput state={pathAsState(state, "metadata.image")} />,
  );
  const row_image = row(
    "image",
    "File",
    "Upload the files you want to mint.",
    "You can upload a file with the following extensions: .pdf, .doc, .docx, .txt, .jpg, .png, .jpeg",
    <ImageInput state={pathAsState(state, "metadata.image$confidential")} />,
  );

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <h2>Create your cNFT</h2>
        <form>
          <table className="table">
            <tbody>
              {row_chain}
              {row_storage}
              {state[0].storage === "IPFS" && row_ipfs_pinning}
              {row_availability}
              {row_title}
              {row_description}
              {row_preview}
              {row_image}
            </tbody>
          </table>
        </form>
        <FileInput state={pathAsState(state, "metadata.files")} />
      </div>
    </div>
  );
}

const row = (
  key: string,
  title: string,
  question: string,
  help: string,
  children: ReactNode,
) => {
  return (
    <tr key={key}>
      <td>{question}</td>
      <td className={"input-wrapper"}>{children}</td>
      <td>
        <Button
          className="info-button"
          onClick={() => dispatchPerunNotice(infoNotice(title, help))}
        >
          ?
        </Button>
      </td>
    </tr>
  );
};
